import { CreditAction, CREDIT_COSTS } from "@/config/pricing";
import { useCredits } from "@/hooks/useCredits";
import { Zap } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface CreditCostBadgeProps {
  action: CreditAction;
  className?: string;
}

/**
 * Shows a small badge with the credit cost for an action.
 * Use next to action buttons so users know the cost before clicking.
 */
export function CreditCostBadge({ action, className }: CreditCostBadgeProps) {
  const { isUnlimited } = useCredits();
  if (isUnlimited) return null;
  const cost = CREDIT_COSTS[action];
  
  return (
    <Badge variant="secondary" className={className}>
      <Zap className="w-3 h-3 mr-1" />
      {cost} credit{cost !== 1 ? "s" : ""}
    </Badge>
  );
}

/**
 * Helper to consume credits before an action.
 * Returns a wrapper function that checks and deducts credits before running the action.
 */
export function useCreditAction() {
  const { consume, hasEnough } = useCredits();

  const withCredits = async (
    action: CreditAction,
    fn: () => Promise<void>,
    description?: string
  ): Promise<boolean> => {
    if (!hasEnough(action)) {
      return false;
    }
    const success = await consume(action, description);
    if (!success) return false;
    await fn();
    return true;
  };

  return { withCredits, hasEnough };
}
